import Badge from "../ui/badge/Badge";
import { ArrowUpIcon, ArrowDownIcon } from "../../icons";

type Polarity = "positive" | "negative" | "neutral";

interface MetricCardProps {
  label: string;
  value: string | number;
  previousValue?: number;
  // Does an increase read as good, bad, or neither? Drives the badge color.
  polarity?: Polarity;
  // One muted line under the value, for a secondary figure the card should carry.
  note?: string;
}

export default function MetricCard({ label, value, previousValue, polarity = "neutral", note }: MetricCardProps) {
  // Pin en-US so grouping is deterministic across runtimes (some CI/test locales
  // render "1 234" instead of "1,234"); this is an English-only admin panel.
  const displayValue = typeof value === "number" ? value.toLocaleString("en-US") : value;
  const pct =
    previousValue !== undefined && previousValue !== 0 && typeof value === "number"
      ? Math.round(((value - previousValue) / previousValue) * 1000) / 10
      : undefined;
  const up = (pct ?? 0) > 0;
  const badgeColor: "success" | "error" | "light" =
    polarity === "neutral" ? "light" : up === (polarity === "positive") ? "success" : "error";

  return (
    <div className="flex h-full flex-col justify-between rounded-2xl border border-gray-200 bg-white p-4 md:p-5 dark:border-gray-800 dark:bg-white/[0.03]">
      <div className="flex min-h-[2.5em] items-start">
        <span className="line-clamp-2 text-sm text-gray-500 dark:text-gray-400">{label}</span>
      </div>
      <div className="mt-2">
        <h4 className="break-words text-2xl font-bold text-gray-800 dark:text-white/90">{displayValue}</h4>
        {note && <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{note}</p>}
        {previousValue !== undefined && (
          <div className="mt-1.5 flex min-h-[1.75rem] items-center">
            {pct !== undefined && pct !== 0 && (
              <Badge color={badgeColor} startIcon={up ? <ArrowUpIcon /> : <ArrowDownIcon />}>
                {up ? "+" : ""}{pct}%
              </Badge>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
